// usePlatoSync Hook
// Provides React Query integration for Plato appointment syncing

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  syncPlatoAppointmentsForDay,
  syncPlatoAppointmentsForWeek,
  isPlatoSyncAvailable,
  getForumClinicId,
  createConflictItem,
} from '@/services/platoService';
import type { PlatoSyncResult, PlatoSyncResultItem, PlatoSyncProgress } from '@/types/plato';

interface UsePlatoSyncOptions {
  clinicId?: string;
  onSuccess?: (result: PlatoSyncResult) => void;
  onError?: (error: Error) => void;
}

export const usePlatoSync = (options: UsePlatoSyncOptions = {}) => {
  const { clinicId, onSuccess, onError } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [lastSyncResult, setLastSyncResult] = useState<PlatoSyncResult | null>(null);
  const [syncProgress, setSyncProgress] = useState<PlatoSyncProgress | null>(null);

  // Check if sync is available
  const isAvailable = isPlatoSyncAvailable();

  // Auto-heal: if a sync attempt fails (most often Plato rate limiting before all
  // pages load), keep retrying with exponential backoff so a SINGLE press of Sync
  // completes on its own instead of leaving a partial/failed sync.
  const SYNC_MAX_RETRIES = 5;
  const syncRetryDelay = (attemptIndex: number) => {
    const delay = Math.min(30_000, 2_000 * 2 ** attemptIndex); // 2s, 4s, 8s, 16s, 30s
    // attemptIndex is 0-based failure count; the NEXT attempt is attemptIndex + 2.
    setSyncProgress({
      stage: 'retrying',
      message: `Plato is rate limiting. Waiting ${Math.round(delay / 1000)}s, then retrying…`,
      attempt: attemptIndex + 1,
      maxAttempts: SYNC_MAX_RETRIES,
    });
    return delay;
  };

  // Mutation for syncing a single day
  const syncDayMutation = useMutation({
    retry: SYNC_MAX_RETRIES,
    retryDelay: syncRetryDelay,
    onMutate: () => setSyncProgress({ stage: 'fetching', message: 'Starting sync…' }),
    mutationFn: async (date: string) => {
      // Get clinic ID - use provided, or fetch Forum clinic ID
      let targetClinicId = clinicId;
      if (!targetClinicId) {
        targetClinicId = await getForumClinicId() || undefined;
      }
      if (!targetClinicId) {
        throw new Error('Could not determine clinic ID for sync');
      }
      return syncPlatoAppointmentsForDay(date, targetClinicId, setSyncProgress);
    },
    onSuccess: (result) => {
      setLastSyncResult(result);

      // Invalidate schedule queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['scheduleItems'] });

      // Note: No toast here - the modal will handle displaying results
      onSuccess?.(result);
    },
    onError: (error: Error) => {
      console.error('[usePlatoSync] Day sync error:', error);
      setSyncProgress({
        stage: 'error',
        message: error.message || 'Failed to sync appointments from Plato.',
      });
      toast({
        title: 'Sync Error',
        description: error.message || 'Failed to sync appointments from Plato.',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });

  // Mutation for syncing a week
  const syncWeekMutation = useMutation({
    retry: SYNC_MAX_RETRIES,
    retryDelay: syncRetryDelay,
    onMutate: () => setSyncProgress({ stage: 'fetching', message: 'Starting sync…' }),
    mutationFn: async (startDate: string) => {
      // Get clinic ID - use provided, or fetch Forum clinic ID
      let targetClinicId = clinicId;
      if (!targetClinicId) {
        targetClinicId = await getForumClinicId() || undefined;
      }
      if (!targetClinicId) {
        throw new Error('Could not determine clinic ID for sync');
      }
      return syncPlatoAppointmentsForWeek(startDate, targetClinicId, setSyncProgress);
    },
    onSuccess: (result) => {
      setLastSyncResult(result);

      // Invalidate schedule queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['scheduleItems'] });

      // Note: No toast here - the modal will handle displaying results
      onSuccess?.(result);
    },
    onError: (error: Error) => {
      console.error('[usePlatoSync] Week sync error:', error);
      setSyncProgress({
        stage: 'error',
        message: error.message || 'Failed to sync appointments from Plato.',
      });
      toast({
        title: 'Sync Error',
        description: error.message || 'Failed to sync appointments from Plato.',
        variant: 'destructive',
      });
      onError?.(error);
    },
  });

  // Mutation for creating a conflict item after user approval
  const createConflictMutation = useMutation({
    mutationFn: async (item: PlatoSyncResultItem) => {
      if (!item.pendingData) {
        throw new Error('No pending data for conflict item');
      }
      return createConflictItem(item.pendingData);
    },
    onSuccess: () => {
      // Invalidate schedule queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['scheduleItems'] });
    },
    onError: (error: Error) => {
      console.error('[usePlatoSync] Create conflict item error:', error);
      toast({
        title: 'Error Creating Item',
        description: error.message || 'Failed to create the schedule item.',
        variant: 'destructive',
      });
    },
  });

  // Helper function to build result message
  const buildResultMessage = (result: PlatoSyncResult): string => {
    const parts: string[] = [];

    if (result.added > 0) {
      parts.push(`${result.added} added`);
    }
    if (result.updated && result.updated > 0) {
      parts.push(`${result.updated} updated`);
    }
    if (result.deleted && result.deleted > 0) {
      parts.push(`${result.deleted} removed`);
    }
    if (result.conflicts > 0) {
      parts.push(`${result.conflicts} conflict${result.conflicts > 1 ? 's' : ''}`);
    }
    if (result.skipped > 0) {
      parts.push(`${result.skipped} skipped`);
    }
    if (result.errors > 0) {
      parts.push(`${result.errors} error${result.errors > 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return 'No changes.';
    }

    return parts.join(', ');
  };

  // Function to clear the last sync result (useful when closing modal)
  const clearLastSyncResult = () => {
    setLastSyncResult(null);
  };

  const clearSyncProgress = () => setSyncProgress(null);

  return {
    // State
    isAvailable,
    lastSyncResult,
    clearLastSyncResult,

    // Live progress (drives the sync-progress modal)
    syncProgress,
    clearSyncProgress,

    // Day sync
    syncDay: syncDayMutation.mutate,
    syncDayAsync: syncDayMutation.mutateAsync,
    isSyncingDay: syncDayMutation.isPending,

    // Week sync
    syncWeek: syncWeekMutation.mutate,
    syncWeekAsync: syncWeekMutation.mutateAsync,
    isSyncingWeek: syncWeekMutation.isPending,

    // Conflict item creation
    createConflictItem: createConflictMutation.mutateAsync,
    isCreatingConflictItem: createConflictMutation.isPending,

    // Combined loading state
    isSyncing: syncDayMutation.isPending || syncWeekMutation.isPending,
  };
};

export default usePlatoSync;
